import type {
  MockClient,
  MockDocument,
  TaxRequestJob,
  FolderNode,
} from "./types";

// Single demo client — keep in sync with src/mocks/store.ts
export const mockClients: MockClient[] = [
  { id: "client-default", name: "Гремлинс Лабс ООД", eik: "207654321" },
];

export const folderTree: FolderNode[] = [
  {
    id: "invoices",
    label: "Invoices",
    path: "invoices",
    children: [
      { id: "invoices-inbound", label: "Inbound", path: "invoices/inbound" },
      { id: "invoices-outbound", label: "Outbound", path: "invoices/outbound" },
    ],
  },
  { id: "contracts", label: "Contracts", path: "contracts" },
  { id: "bank-statements", label: "Bank Statements", path: "bank-statements" },
  { id: "annexes", label: "Annexes", path: "annexes" },
  { id: "agreements", label: "Agreements", path: "agreements" },
  { id: "other", label: "Other", path: "other" },
];

// Documents drawn from the real PDFs in public/invoices/ and the DSK bank
// statement in public/bank/. Counterparties, dates and amounts mirror the
// extracted-invoice fixtures in src/mocks/fixtures/invoices.ts.
export const mockDocuments: MockDocument[] = [
  // ---- Inbound vendor invoices (SaaS / infra) ---------------------------
  {
    id: "d1",
    originalFilename: "Hetzner_2026-04-25_086000816439.pdf",
    storedFilename: "2026-04-25-086000816439-Hetzner.pdf",
    documentType: "invoice_inbound",
    documentDate: "2026-04-25",
    uploadDate: "2026-04-26",
    counterpartyName: "Hetzner Online GmbH",
    counterpartyEik: "DE812871812",
    amount: 32.13,
    currency: "EUR",
    status: "processed",
    clientId: "client-default",
    folder: "invoices/inbound",
    aiConfidence: 0.97,
    description: "Dedicated server hosting — April 2026",
  },
  {
    id: "d2",
    originalFilename: "invoice_3191-18539_ScreenshotOne.pdf",
    storedFilename: "2026-04-21-3191-18539-ScreenshotOne.pdf",
    documentType: "invoice_inbound",
    documentDate: "2026-04-21",
    uploadDate: "2026-04-22",
    counterpartyName: "Paddle.com Market Ltd (ScreenshotOne)",
    counterpartyEik: "GB117987373",
    amount: 17.0,
    currency: "USD",
    status: "processed",
    clientId: "client-default",
    folder: "invoices/inbound",
    aiConfidence: 0.95,
    description: "ScreenshotOne API — monthly subscription",
  },
  {
    id: "d3",
    originalFilename: "Invoice-RMGKTU-00016.pdf",
    storedFilename: "2026-04-05-RMGKTU-00016-Supabase.pdf",
    documentType: "invoice_inbound",
    documentDate: "2026-04-05",
    uploadDate: "2026-04-06",
    counterpartyName: "Supabase Pte Ltd",
    counterpartyEik: "SG202018003N",
    amount: 34.97,
    currency: "USD",
    status: "processed",
    clientId: "client-default",
    folder: "invoices/inbound",
    aiConfidence: 0.96,
    description: "Supabase Pro plan — April 2026",
  },

  // ---- Outbound (Paddle merchant-of-record invoices we issued) ----------
  {
    id: "d4",
    originalFilename: "paddle_payment_220820_gb_invoice.pdf",
    storedFilename: "2026-04-02-220820-GB-Paddle.pdf",
    documentType: "invoice_outbound",
    documentDate: "2026-04-02",
    uploadDate: "2026-04-03",
    counterpartyName: "Paddle.com Market Limited (UK)",
    counterpartyEik: "GB117987373",
    amount: 142.85,
    currency: "EUR",
    status: "processed",
    clientId: "client-default",
    folder: "invoices/outbound",
    aiConfidence: 0.98,
    description: "Paddle UK payout invoice — March 2026 sales",
  },
  {
    id: "d5",
    originalFilename: "paddle_payment_220820_us_invoice.pdf",
    storedFilename: "2026-04-02-220820-US-Paddle.pdf",
    documentType: "invoice_outbound",
    documentDate: "2026-04-02",
    uploadDate: "2026-04-03",
    counterpartyName: "Paddle.com Inc. (US)",
    counterpartyEik: "US453361423",
    amount: 120.85,
    currency: "EUR",
    status: "processed",
    clientId: "client-default",
    folder: "invoices/outbound",
    aiConfidence: 0.98,
    description: "Paddle US payout invoice — March 2026 sales",
  },
  {
    id: "d6",
    originalFilename: "paddle_payment_220820_remittance_advice.pdf",
    storedFilename: "2026-04-07-RA-220820-Paddle.pdf",
    documentType: "other",
    documentDate: "2026-04-07",
    uploadDate: "2026-04-08",
    counterpartyName: "Paddle.com Market Limited",
    counterpartyEik: "GB117987373",
    amount: 263.7,
    currency: "EUR",
    status: "processed",
    clientId: "client-default",
    folder: "other",
    aiConfidence: 0.93,
    description: "Paddle remittance advice — combined UK + US payout",
  },

  // ---- Bank statement ---------------------------------------------------
  {
    id: "d7",
    originalFilename: "2026.04-dsk-bank.csv",
    storedFilename: "2026-04-30-Izvlechenie-DSK-042026.csv",
    documentType: "bank_statement",
    documentDate: "2026-04-30",
    uploadDate: "2026-05-02",
    counterpartyName: "Банка ДСК",
    counterpartyEik: "121830616",
    amount: 0,
    currency: "EUR",
    status: "processed",
    clientId: "client-default",
    folder: "bank-statements",
    aiConfidence: 0.91,
    description: "ДСК Банк извлечение — Април 2026 (EUR сметка)",
  },

  // ---- Contracts / agreements for context --------------------------------
  {
    id: "d8",
    originalFilename: "dogovor_naem_office.pdf",
    storedFilename: "2025-09-01-Dogovor-Naem-Office-Sofia.pdf",
    documentType: "contract",
    documentDate: "2025-09-01",
    uploadDate: "2025-09-02",
    counterpartyName: "Изток Пропърти Мениджмънт ЕООД",
    counterpartyEik: "204556712",
    amount: 1800.0,
    currency: "BGN",
    status: "processed",
    clientId: "client-default",
    folder: "contracts",
    aiConfidence: 0.92,
    description: "Договор за наем — офис ж.к. Изток, ет. 11 (24 месеца)",
  },
  {
    id: "d9",
    originalFilename: "hetzner_msa.pdf",
    storedFilename: "2025-06-15-MSA-Hetzner.pdf",
    documentType: "agreement",
    documentDate: "2025-06-15",
    uploadDate: "2025-06-16",
    counterpartyName: "Hetzner Online GmbH",
    counterpartyEik: "DE812871812",
    amount: 0,
    currency: "EUR",
    status: "processed",
    clientId: "client-default",
    folder: "agreements",
    aiConfidence: 0.88,
    description: "Master Service Agreement — Hetzner dedicated hosting",
  },
  {
    id: "d10",
    originalFilename: "aneks_1_naem_office.pdf",
    storedFilename: "2026-03-01-Aneks-1-Dogovor-Naem.pdf",
    documentType: "annex",
    documentDate: "2026-03-01",
    uploadDate: "2026-03-02",
    counterpartyName: "Изток Пропърти Мениджмънт ЕООД",
    counterpartyEik: "204556712",
    amount: 1980.0,
    currency: "BGN",
    status: "processed",
    clientId: "client-default",
    folder: "annexes",
    aiConfidence: 0.9,
    description: "Анекс 1 — индексация на наема (+10%)",
  },

  // ---- In-flight items --------------------------------------------------
  {
    id: "d11",
    originalFilename: "scan_invoice_may.pdf",
    storedFilename: "",
    documentType: "invoice_inbound",
    documentDate: "2026-05-12",
    uploadDate: "2026-05-13",
    counterpartyName: "",
    counterpartyEik: "",
    amount: 0,
    currency: "EUR",
    status: "processing",
    clientId: "client-default",
    folder: "invoices/inbound",
    aiConfidence: 0,
    description: "",
  },
  {
    id: "d12",
    originalFilename: "corrupted_receipt.tiff",
    storedFilename: "corrupted_receipt.tiff",
    documentType: "other",
    documentDate: "2026-05-08",
    uploadDate: "2026-05-08",
    counterpartyName: "",
    counterpartyEik: "",
    amount: 0,
    currency: "BGN",
    status: "failed",
    clientId: "client-default",
    folder: "other",
    aiConfidence: 0,
    description: "",
  },
];

export const mockTaxRequestJobs: TaxRequestJob[] = [
  {
    id: "tr1",
    clientId: "client-default",
    requestFilename: "NAP_Iskane_2026_042.pdf",
    requestDate: "2026-05-10",
    status: "completed",
    totalRequested: 5,
    totalFound: 5,
    totalMissing: 0,
    totalAmbiguous: 0,
    items: [
      {
        id: "tr1-i1",
        requestedDescription: "Фактура №086000816439 от 25.04.2026 от Hetzner Online GmbH",
        requestedDocumentType: "invoice_inbound",
        requestedDocumentNumber: "086000816439",
        requestedDocumentDate: "2026-04-25",
        requestedCounterparty: "Hetzner Online GmbH",
        status: "found",
        matchedDocumentId: "d1",
        matchNotes: null,
        candidateDocumentIds: [],
      },
      {
        id: "tr1-i2",
        requestedDescription: "Фактура №RMGKTU-00016 от 05.04.2026 от Supabase Pte Ltd",
        requestedDocumentType: "invoice_inbound",
        requestedDocumentNumber: "RMGKTU-00016",
        requestedDocumentDate: "2026-04-05",
        requestedCounterparty: "Supabase Pte Ltd",
        status: "found",
        matchedDocumentId: "d3",
        matchNotes: null,
        candidateDocumentIds: [],
      },
      {
        id: "tr1-i3",
        requestedDescription: "Издадена фактура №220820-GB от 02.04.2026 (Paddle UK)",
        requestedDocumentType: "invoice_outbound",
        requestedDocumentNumber: "220820-GB",
        requestedDocumentDate: "2026-04-02",
        requestedCounterparty: "Paddle.com Market Limited (UK)",
        status: "found",
        matchedDocumentId: "d4",
        matchNotes: null,
        candidateDocumentIds: [],
      },
      {
        id: "tr1-i4",
        requestedDescription: "Банково извлечение ДСК за Април 2026",
        requestedDocumentType: "bank_statement",
        requestedDocumentNumber: null,
        requestedDocumentDate: "2026-04-30",
        requestedCounterparty: "Банка ДСК",
        status: "found",
        matchedDocumentId: "d7",
        matchNotes: null,
        candidateDocumentIds: [],
      },
      {
        id: "tr1-i5",
        requestedDescription: "Договор за наем на офис с Изток Пропърти Мениджмънт",
        requestedDocumentType: "contract",
        requestedDocumentNumber: null,
        requestedDocumentDate: null,
        requestedCounterparty: "Изток Пропърти Мениджмънт ЕООД",
        status: "found",
        matchedDocumentId: "d8",
        matchNotes: null,
        candidateDocumentIds: [],
      },
    ],
  },
  {
    id: "tr2",
    clientId: "client-default",
    requestFilename: "NAP_Iskane_2026_058.pdf",
    requestDate: "2026-05-14",
    status: "completed_with_issues",
    totalRequested: 6,
    totalFound: 3,
    totalMissing: 2,
    totalAmbiguous: 1,
    items: [
      {
        id: "tr2-i1",
        requestedDescription: "ScreenshotOne абонамент за Април 2026",
        requestedDocumentType: "invoice_inbound",
        requestedDocumentNumber: "3191-18539",
        requestedDocumentDate: "2026-04-21",
        requestedCounterparty: "ScreenshotOne",
        status: "found",
        matchedDocumentId: "d2",
        matchNotes: null,
        candidateDocumentIds: [],
      },
      {
        id: "tr2-i2",
        requestedDescription: "Издадена фактура №220820-US от 02.04.2026 (Paddle US)",
        requestedDocumentType: "invoice_outbound",
        requestedDocumentNumber: "220820-US",
        requestedDocumentDate: "2026-04-02",
        requestedCounterparty: "Paddle.com Inc. (US)",
        status: "found",
        matchedDocumentId: "d5",
        matchNotes: null,
        candidateDocumentIds: [],
      },
      {
        id: "tr2-i3",
        requestedDescription: "Анекс №1 към договора за наем (индексация)",
        requestedDocumentType: "annex",
        requestedDocumentNumber: null,
        requestedDocumentDate: "2026-03-01",
        requestedCounterparty: "Изток Пропърти Мениджмънт ЕООД",
        status: "found",
        matchedDocumentId: "d10",
        matchNotes: null,
        candidateDocumentIds: [],
      },
      {
        id: "tr2-i4",
        requestedDescription: "Фактура от GitHub Inc. за Април 2026",
        requestedDocumentType: "invoice_inbound",
        requestedDocumentNumber: null,
        requestedDocumentDate: "2026-04-15",
        requestedCounterparty: "GitHub Inc.",
        status: "not_found",
        matchedDocumentId: null,
        matchNotes: "No matching invoice from GitHub in the repository for this period",
        candidateDocumentIds: [],
      },
      {
        id: "tr2-i5",
        requestedDescription: "Извлечение от втора банкова сметка (USD) за Април 2026",
        requestedDocumentType: "bank_statement",
        requestedDocumentNumber: null,
        requestedDocumentDate: "2026-04-30",
        requestedCounterparty: null,
        status: "not_found",
        matchedDocumentId: null,
        matchNotes: "Only the EUR DSK statement is on file — USD account statement is missing",
        candidateDocumentIds: [],
      },
      {
        id: "tr2-i6",
        requestedDescription: "Споразумение / договор с доставчик на хостинг услуги",
        requestedDocumentType: "agreement",
        requestedDocumentNumber: null,
        requestedDocumentDate: null,
        requestedCounterparty: "Hetzner",
        status: "ambiguous",
        matchedDocumentId: null,
        matchNotes: "Two candidates found — MSA and the April hosting invoice. Pick the contract document.",
        candidateDocumentIds: ["d9", "d1"],
      },
    ],
  },
  {
    id: "tr3",
    clientId: "client-default",
    requestFilename: "NAP_Iskane_2026_064.pdf",
    requestDate: "2026-05-17",
    status: "processing",
    totalRequested: 4,
    totalFound: 0,
    totalMissing: 0,
    totalAmbiguous: 0,
    items: [],
  },
];

export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  invoice_inbound: "Invoice (Inbound)",
  invoice_outbound: "Invoice (Outbound)",
  contract: "Contract",
  bank_statement: "Bank Statement",
  annex: "Annex",
  agreement: "Agreement",
  other: "Other",
};
